import { User, _User } from '../interfaces/user';
import { get, set } from 'lodash';
import { removeEmpty } from '../../utils';
import { instance } from './instance';

const getStore = () => {
  return get(window, 'STORE', { getState: () => {} });
};

export const getUser = async (id: string) => {
  const resp = await instance.get(`/user/${id}`, { data: {} }).catch(error => {
    if (error.request.response.includes('Could not retrieve user')) {
      throw new Error('UserNotFoundException');
    } else {
      throw new Error('Network Error');
    }
  });
  return new _User(get(resp, 'data.user', null)) as User;
};

export const getUsers = async (where?: {}) => {
  let resp = {};
  if (where) {
    resp = await instance.post(`/user`, {
      where,
    });
  } else {
    resp = await instance.get(`/user`, { data: {} });
  }
  return get(resp, 'data.users', []).map(
    (u: User | undefined) => new _User(u)
  ) as User[];
};

export const getAllUsers = async (where?: {}): Promise<User[]> => {
  return getUsers(where);
};

export const createUser = async (user: User) => {
  const resp = await instance.post('/user/create', {
    user: removeEmpty(user),
    // user,
  });
  return get(resp, 'data.user', null) as User;
};

export const updateUser = (user: User) => {
  return createUser(user);
};

// AER-1754: stamp ONE timestamp on the signed-in user's record, server-side.
//
// This used to `set(user, key, ...)` and then POST the whole redux user to /user/create.
// That is a full-item PUT whose sort key the server recomputes from the posted orgId
// (`getSK(orgId, userId)`), and `_User` initialises `orgId = ''` — which AER-1701 now
// keeps on the wire instead of stripping. So a redux user that had lost its org wrote a
// SECOND, org-less `#<email>` row on every dashboard load and on every sign-out. '#'
// sorts below every hex digit, so that leftover then won the `pk="users"` lookup and the
// user could never see their org again: pinned to Organization Details, minting a
// duplicate org on each retry.
//
// POST /user/activity resolves the record server-side and writes the single attribute
// with updateItemFields (ConditionExpression attribute_exists(pk)), so a heartbeat can
// no longer create, move or resurrect a user record — and no longer rewrites the whole
// record through USER_WRITABLE_FIELDS, where an omission is a deletion.
//
// DEPLOY ORDER: the backend must ship first. Against an older backend this 404s, which
// costs the lastActivity / signedOutAt writes (degrading the AER-1599 push gate) but
// never blocks sign-out or dashboard load — both call sites tolerate a rejection.
//
// The local `set` is kept, using the timestamp the server actually stored: redux's copy
// of the user is posted verbatim by ~24 other screens, and `lastActivity` / `signedOutAt`
// are on USER_WRITABLE_FIELDS, so leaving a stale value in redux would let the next
// full-record save walk the server's timestamp backwards.
export const updateUserActivity = async (key = 'lastActivity') => {
  const user = get(getStore().getState(), 'auth.user');
  const email = get(user, 'email');
  if (!user || !email) {
    return;
  }

  const resp = await instance.post('/user/activity', { email, key });

  const at = get(resp, 'data.at');
  if (at) {
    set(user, key, at);
  }
}

export const deleteUser = async (sk: string) => {
  const resp = await instance.post(`user/delete`, { sk });
  return resp;
};

// AER-1819: delete the signed-in user's own account. The server works out whose account from
// the Cognito access token, so there is deliberately no user id parameter.
export const deleteOwnAccount = async (accessToken: string) => {
  const resp = await instance.post('/user/deleteaccount', { accessToken });
  return resp;
};

export const getToken = async (data = {}) => {
  const resp = await instance.post('/user/token', { ...data });
  return get(resp, 'data.token');
};

export const generatePinAndSendMsgToUser = async (
  email: string,
  useSMSIfAvail = true
) => {
  const resp = await instance.post('/user/generatepin', {
    email,
    useSMSIfAvail,
  });
  return get(resp, 'data', {});
};

export const validateCode = async (email: string, code: string) => {
  const resp = await instance.post('/user/validatecode', { email, code });
  return !!get(resp, 'data.valid', false);
};
